import React from "react";
import { Lock, CheckCircle, PencilSimple } from "@phosphor-icons/react";

export const REQUIRED_DOCS = ["MC Authority", "Insurance COI", "W-9", "Owner CDL", "Signed Agreement", "Bank / Voided Check", "NOA / Factoring Letter"];
export const EXPIRY_DOCS = ["Insurance COI", "Owner CDL"];
export const CRITICAL_FLAGS = ["Double brokering history", "Chameleon carrier indicators", "Authority revoked / out-of-service"];
export const WARNING_FLAGS = ["Address mismatch with FMCSA", "Phone mismatch with FMCSA", "New authority (under 90 days)", "Recent insurance cancellation", "Shared address/phone with another carrier", "Unverifiable references"];
export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
export const today = () => new Date().toISOString().slice(0, 10);
export const authorityDays = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);

export function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid={testid}>
      {options.map(([v, label, tone]) => (
        <button key={label} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${String(v).replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}
          className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${value === v ? `${tone} text-white shadow-sm` : "text-slate-600 hover:text-slate-900"}`}>{label}</button>
      ))}
    </div>
  );
}

export const Row = ({ label, children, hint }) => (
  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 py-3 border-b border-slate-100 last:border-0">
    <div><p className="text-sm font-semibold text-slate-800">{label}</p>{hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}</div>
    <div className="md:max-w-[60%]">{children}</div>
  </div>
);

export function StepShell({ n, id, title, subtitle, state, summary, error, canConfirm, onConfirm, onEdit, children, hideConfirm }) {
  const locked = state === "locked", done = state === "done";
  return (
    <section data-testid={`ca-step-${id}`} className={`relative rounded-2xl border transition-[border-color,box-shadow] ${locked ? "border-slate-200 bg-slate-50/60" : done ? "border-emerald-200 bg-white" : "border-slate-900 bg-white shadow-[0_8px_30px_-12px_rgba(15,23,42,0.35)]"}`}>
      <header className="flex items-center gap-4 px-5 py-4">
        <span className={`w-10 h-10 rounded-xl grid place-items-center font-mono font-extrabold text-sm shrink-0 ${locked ? "bg-slate-200 text-slate-400" : done ? "bg-emerald-600 text-white" : "bg-slate-900 text-white"}`}>
          {locked ? <Lock size={16} weight="fill" /> : done ? <CheckCircle size={20} weight="fill" /> : String(n).padStart(2, "0")}
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-heading font-bold ${locked ? "text-slate-400" : "text-slate-900"}`}>{title}</p>
          <p className="text-xs text-slate-500 truncate">{done ? summary : locked ? "Locked. Finish the previous step first." : subtitle}</p>
        </div>
        {done && onEdit && <button onClick={onEdit} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-blue-600" data-testid={`ca-step-${id}-edit`}><PencilSimple size={13} />Edit</button>}
      </header>
      {state === "active" && (
        <div className="px-5 pb-5">
          <div className="border-t border-slate-100 pt-2">{children}</div>
          {!hideConfirm && (
            <div className="flex items-center justify-between gap-3 mt-4">
              <p className={`text-xs ${error ? "text-red-600" : "text-emerald-600"}`} data-testid={`ca-step-${id}-status`}>{error || "Step requirements met."}</p>
              <button onClick={onConfirm} disabled={!canConfirm} data-testid={`ca-step-${id}-confirm`}
                className="h-10 px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-800 transition-colors">Confirm &amp; Continue</button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
